/**
 * Catálogo de vigencias comerciales (Flexible / Preferencial / Commit / plazos en meses).
 * Settings puede ampliar la lista; estos defaults nunca deben desaparecer del combo.
 *
 * Preferencial es flexible con descuento y plazo. No es un SKU: aplica al precio flex
 * de cada producto de la cotización (Starter, Standard, etc.).
 * La misma regla en el servidor vive en server/utils/commercial-term-policy.js.
 */

export const DEFAULT_COMMERCIAL_TERMS = [
  { id: 'default-flexible', label: 'Flexible', type: 'flex', value: -2 },
  { id: 'default-preferencial-12', label: 'Preferencial 12 meses', type: 'preferencial', value: 12 },
  { id: 'default-preferencial-24', label: 'Preferencial 24 meses', type: 'preferencial', value: 24 },
  { id: 'default-preferencial-36', label: 'Preferencial 36 meses', type: 'preferencial', value: 36 },
  { id: 'default-commit', label: 'Commit', type: 'commit', value: -1 },
  { id: 'default-12', label: '12 meses', type: 'termed', value: 12 },
  { id: 'default-24', label: '24 meses', type: 'termed', value: 24 },
  { id: 'default-36', label: '36 meses', type: 'termed', value: 36 },
];

/** Compara etiquetas de vigencia sin importar mayúsculas ni espacios. */
export const normalizeTermLabel = (label) =>
  String(label || '').trim().toLowerCase().replace(/\s+/g, ' ');

export const inferTermTypeFromLabel = (label) => {
  const lower = normalizeTermLabel(label);
  if (lower.includes('preferenc')) return 'preferencial';
  if (!lower || lower.includes('flex')) return 'flex';
  if (lower === 'commit' || (lower.includes('commit') && !lower.includes('mes'))) return 'commit';
  return 'termed';
};

export const policyForType = (type) => {
  const isPreferencial = type === 'preferencial';
  const isFlex = type === 'flex';
  const isCommitPure = type === 'commit';
  const usesFlexPrice = isFlex || isPreferencial;
  return {
    type,
    isPreferencial,
    isFlex,
    isCommitPure,
    disableLicenseDiscount: isFlex || isCommitPure,
    priceType: usesFlexPrice ? 'flex' : 'commit',
    esquema: isPreferencial ? 'Preferencial' : (isFlex ? 'Flex' : 'Commit'),
    gwsPlan: usesFlexPrice ? 'FLEXIBLE' : 'ANNUAL_MONTHLY_PAY'
  };
};

export const policyFromLabel = (label) => policyForType(inferTermTypeFromLabel(label));

export const findTermOption = (vigenciaLabel, termsOptions = []) => {
  const needle = normalizeTermLabel(vigenciaLabel);
  if (!needle) return null;
  return (termsOptions || []).find((t) => normalizeTermLabel(t.label) === needle) || null;
};

const termSortRank = (t) => {
  const type = t?.type || inferTermTypeFromLabel(t?.label);
  if (type === 'flex') return -2;
  if (type === 'preferencial') {
    const months = Number(t?.value);
    const span = Number.isFinite(months) && months > 0 ? months : 12;
    return -1.5 + span / 1000;
  }
  if (type === 'commit') return -1;
  const fromValue = Number(t?.value);
  if (Number.isFinite(fromValue) && fromValue > 0) return fromValue;
  const lower = normalizeTermLabel(t?.label);
  if (lower === 'anual') return 12;
  return parseInt(String(t?.label || ''), 10) || 999;
};

export const sortCommercialTerms = (terms = []) =>
  [...(Array.isArray(terms) ? terms : [])].sort((a, b) => termSortRank(a) - termSortRank(b));

/** Une lo de Firestore con Flexible, Preferencial, Commit y 12/24/36 meses para que el combo no quede ciego. */
export const mergeCommercialTerms = (fromDb = []) => {
  const list = Array.isArray(fromDb) ? [...fromDb] : [];
  for (const fallback of DEFAULT_COMMERCIAL_TERMS) {
    if (!findTermOption(fallback.label, list)) list.push({ ...fallback });
  }
  return sortCommercialTerms(list);
};

export const canonicalTermLabel = (vigenciaLabel, termsOptions = []) => {
  const matched = findTermOption(vigenciaLabel, termsOptions);
  return matched?.label || vigenciaLabel || 'Flexible';
};

/** Opciones del selector de vigencia, con defaults y un valor legado que no esté en el catálogo. */
export const termSelectOptions = (termsOptions = [], currentLabel) => {
  const list = mergeCommercialTerms(termsOptions);
  const current = String(currentLabel || '').trim();
  if (current && !findTermOption(current, list)) {
    list.push({
      id: `legacy-${normalizeTermLabel(current)}`,
      label: current,
      type: inferTermTypeFromLabel(current)
    });
  }
  return list;
};

export const resolveTermType = (vigenciaLabel, termsOptions = []) =>
  findTermOption(vigenciaLabel, termsOptions)?.type || inferTermTypeFromLabel(vigenciaLabel);

export const resolveTermDiscountPolicy = (vigenciaLabel, termsOptions = []) => {
  const type = resolveTermType(vigenciaLabel, termsOptions);
  return policyForType(type);
};

/** % al cliente que sí entra al precio. Commit y Flexible lo dejan en 0 aunque el campo siga guardado. */
export const appliedClientDiscountPct = (descuento, vigenciaLabel, termsOptions = []) => {
  const { disableLicenseDiscount } = resolveTermDiscountPolicy(vigenciaLabel, termsOptions);
  if (disableLicenseDiscount) return 0;
  const n = Number(descuento);
  return Number.isFinite(n) ? n : 0;
};
